/**
 * Index helpers — centralized index calculation for all DnD and insert operations.
 * INTERNAL — not exported from operations/index.ts (except computeGhostIndex, getRealNeighborIndexes).
 */

import { getDb } from "../runtime.js"
import { ObjectType, ObjectStatus } from "../db-types.js"
import { nextIndex, distributeIndexes } from "../utils.js"
import type { InsertPosition } from "../op-types.js"
import type { DbObject } from "../db-types.js"
import type { Collection } from "dexie"

/**
 * Calculate a fractional midpoint between two optional boundaries.
 */
export function calculateMidpoint(
  before: number | undefined,
  after: number | undefined,
): number {
  return nextIndex(before, after)
}

export { distributeIndexes }

/**
 * Rows read in the first look at the edge of an index range. An insert wants the first LIVE row on one
 * side of a point, and that is nearly always the very first row: a LIMIT read of this size finds it
 * without touching the rest of the container (2,500+ siblings in a big project, ~3,600 in Anyday).
 */
const EDGE_WINDOW = 8

/**
 * The first row, in the order `make()` builds, that `accept` takes: a LIMIT window first and, only when
 * every row of a full window was refused (a run of trashed rows at the edge), the filtered walk the
 * whole-list version always did. `make` must build a FRESH collection per call (Dexie's reverse()
 * changes the collection it is called on). The answer is the same as walking the whole range.
 */
export async function firstAccepted(
  make: () => Collection<DbObject, string>,
  accept: (o: DbObject) => boolean,
  window: number = EDGE_WINDOW,
): Promise<DbObject | undefined> {
  const head = await make().limit(window).toArray()
  const hit = head.find(accept)
  if (hit || head.length < window) return hit
  return make().filter(accept).first()
}

const isLive = (o: DbObject): boolean => o.isDeleted === 0

/**
 * Calculate the numeric index for placing an item at a given InsertPosition.
 */
export async function getInsertIndex(
  parentPath: string,
  position: InsertPosition,
  _itemType: ObjectType,
  field: "index" | "startAtIndex" = "index",
): Promise<number> {
  if (field === "startAtIndex") {
    return getInsertIndexStartAt(position)
  }

  const db = getDb()

  // Every sibling row of `parentPath` in index order, as a fresh Collection (Dexie's reverse() changes
  // the collection it is called on, so each read builds its own).
  const siblingsIn = (lower: number, upper: number, includeLower: boolean, includeUpper: boolean) =>
    db.totodo
      .where("[parentPath+index]")
      .between([parentPath, lower], [parentPath, upper], includeLower, includeUpper)

  // "top" and "bottom" only need the row at the matching end of the [parentPath, index] index: a LIMIT 1
  // read, so the cost of an insert does not grow with the container (2,500+ rows in a big project, ~3,600
  // in Anyday were delivered for every new task). It is deliberately NOT filtered on isDeleted: the rows
  // at the edge are often trashed ones (recently added, recently deleted), and skipping a run of them
  // would take a scan. A trashed row at the edge only leaves a gap in the index values: the new row still
  // sorts after / before every live sibling, and it no longer shares an index with a trashed one that a
  // restore would bring back. Rows are ordered by index, never by distance, so a gap is invisible.
  if (position === "top" || position === "bottom") {
    const [edge] = await (position === "top"
      ? siblingsIn(-Infinity, Infinity, true, true)
      : siblingsIn(-Infinity, Infinity, true, true).reverse()
    )
      .limit(1)
      .toArray()
    if (!edge) return 0
    return position === "top" ? edge.index - 1 : edge.index + 1
  }

  if (position === "beforeFirstArea") {
    // Only used for the root containers (a handful of rows): the full ordered list is cheap there
    const siblings = await siblingsIn(-Infinity, Infinity, true, true)
      .filter((o) => o.isDeleted === 0)
      .sortBy("index")
    if (siblings.length === 0) return 0
    const firstArea = siblings.find((s) => s.type === ObjectType.Area)
    if (!firstArea) {
      return siblings[siblings.length - 1]!.index + 1
    }
    const areaIdx = siblings.indexOf(firstArea)
    const prev = areaIdx > 0 ? siblings[areaIdx - 1] : undefined
    return prev
      ? calculateMidpoint(prev.index, firstArea.index)
      : firstArea.index - 1
  }

  // afterId / beforeId: the anchor row (it must be a live sibling in the index, as before), then the live
  // row next to it on the wanted side. Rows that share the anchor's index are skipped: the new value is
  // a midpoint between two DIFFERENT indexes (the old list walk could pick a tied row and return the
  // anchor's own index).
  const anchorId = "afterId" in position ? position.afterId : "beforeId" in position ? position.beforeId : undefined
  if (anchorId === undefined) return 0
  const anchor = await db.totodo.get(anchorId)
  const anchored =
    anchor !== undefined &&
    anchor.parentPath === parentPath &&
    anchor.isDeleted === 0 &&
    Number.isFinite(anchor.index)
  if ("afterId" in position) {
    if (!anchored) return getInsertIndex(parentPath, "bottom", _itemType)
    const next = await firstAccepted(() => siblingsIn(anchor.index, Infinity, false, true), isLive)
    return next ? calculateMidpoint(anchor.index, next.index) : anchor.index + 1
  }
  if (!anchored) return getInsertIndex(parentPath, "top", _itemType)
  const prev = await firstAccepted(() => siblingsIn(-Infinity, anchor.index, true, false).reverse(), isLive)
  return prev ? calculateMidpoint(prev.index, anchor.index) : anchor.index - 1
}

/**
 * Calculate insert index for the startAtIndex table.
 */
async function getInsertIndexStartAt(
  position: InsertPosition,
): Promise<number> {
  const db = getDb()

  if (position === "top") {
    const first = await db.startAtIndex.orderBy("index").first()
    return first ? first.index - 1 : 0
  }

  if (position === "bottom" || position === "beforeFirstArea") {
    const last = await db.startAtIndex.orderBy("index").last()
    return last ? last.index + 1 : 0
  }

  if ("afterId" in position) {
    const afterEntry = await db.startAtIndex.get(`#${position.afterId}`)
    if (!afterEntry) {
      const last = await db.startAtIndex.orderBy("index").last()
      return last ? last.index + 1 : 0
    }
    const nextEntries = await db.startAtIndex
      .where("index")
      .above(afterEntry.index)
      .limit(1)
      .toArray()
    return nextEntries.length > 0
      ? calculateMidpoint(afterEntry.index, nextEntries[0]!.index)
      : afterEntry.index + 1
  }

  if ("beforeId" in position) {
    const beforeEntry = await db.startAtIndex.get(`#${position.beforeId}`)
    if (!beforeEntry) {
      const first = await db.startAtIndex.orderBy("index").first()
      return first ? first.index - 1 : 0
    }
    const prevEntries = await db.startAtIndex
      .where("index")
      .below(beforeEntry.index)
      .reverse()
      .limit(1)
      .toArray()
    return prevEntries.length > 0
      ? calculateMidpoint(prevEntries[0]!.index, beforeEntry.index)
      : beforeEntry.index - 1
  }

  return 0
}

/**
 * Calculate the privateIndex value for inserting a new container before
 * the first Area in the sidebar's effective sort order.
 */
export async function getSidebarInsertIndex(): Promise<number> {
  const db = getDb()

  const containers = await db.totodo
    .where("[parentPath+type+isDeleted]")
    .anyOf([
      ["root", ObjectType.Area, 0],
      ["root", ObjectType.Project, 0],
      ["root", ObjectType.List, 0],
    ])
    .filter((item) => item.status === ObjectStatus.Unfinished)
    .toArray()

  if (containers.length === 0) return 0

  const pIds = containers.map((c) => `#${c.id}`)
  const pEntries = await db.privateIndex.bulkGet(pIds)
  const pMap = new Map<string, number>()
  pIds.forEach((pid, i) => {
    if (pEntries[i]) pMap.set(pid, pEntries[i]!.index)
  })

  const sorted = containers
    .map((c) => ({
      id: c.id!,
      type: c.type,
      effectiveIndex: pMap.get(`#${c.id}`) ?? c.index,
    }))
    .sort((a, b) => a.effectiveIndex - b.effectiveIndex)

  const firstArea = sorted.find((s) => s.type === ObjectType.Area)
  if (!firstArea) {
    return sorted[sorted.length - 1]!.effectiveIndex + 1
  }

  const areaIdx = sorted.indexOf(firstArea)
  const prev = areaIdx > 0 ? sorted[areaIdx - 1] : undefined
  return prev
    ? calculateMidpoint(prev.effectiveIndex, firstArea.effectiveIndex)
    : firstArea.effectiveIndex - 1
}

/**
 * Compute a ghost index for a given field — placed below MIN(existing).
 */
export async function computeGhostIndex(
  ghostField: "index" | "startAtIndex",
  parentPath?: string,
): Promise<number> {
  const db = getDb()

  if (ghostField === "index" && parentPath) {
    const siblings = await db.totodo
      .where("[parentPath+type]")
      .between(
        [parentPath, ObjectType.Subtask],
        [parentPath, ObjectType.Section],
        true,
        true,
      )
      .toArray()

    if (siblings.length === 0) return -1
    const minIdx = Math.min(...siblings.map((s) => s.index))
    return minIdx - 1
  }

  if (ghostField === "startAtIndex") {
    const first = await db.startAtIndex.orderBy("index").first()
    if (!first) return -1
    return first.index - 1
  }

  return -1
}

/**
 * When a filter hides some tasks, find the real DB neighbors for midpoint calculation.
 */
export async function getRealNeighborIndexes(
  field: "index" | "startAtIndex",
  prevVisibleIdx: number | undefined,
  nextVisibleIdx: number | undefined,
  excludeIds: Set<string>,
  parentPath?: string,
): Promise<{ realPrev: number | undefined; realNext: number | undefined }> {
  const db = getDb()

  if (field === "index" && parentPath) {
    const siblings = await db.totodo
      .where("[parentPath+type]")
      .between(
        [parentPath, ObjectType.Subtask],
        [parentPath, ObjectType.Section],
        true,
        true,
      )
      .filter((t) => t.isDeleted === 0 && !excludeIds.has(t.id ?? ""))
      .toArray()

    const sorted = siblings.sort((a, b) => a.index - b.index)

    let realPrev = prevVisibleIdx
    if (nextVisibleIdx !== undefined) {
      for (const s of sorted) {
        if (
          s.index < nextVisibleIdx &&
          (prevVisibleIdx === undefined || s.index >= prevVisibleIdx)
        ) {
          realPrev = s.index
        }
      }
    }

    let realNext = nextVisibleIdx
    if (prevVisibleIdx !== undefined) {
      for (const s of sorted) {
        if (
          s.index > prevVisibleIdx &&
          (nextVisibleIdx === undefined || s.index <= nextVisibleIdx)
        ) {
          realNext = s.index
          break
        }
      }
    }

    return { realPrev, realNext }
  }

  if (field === "startAtIndex") {
    const all = await db.startAtIndex
      .orderBy("index")
      .filter((s) => !excludeIds.has(s.id ?? ""))
      .toArray()

    let realPrev = prevVisibleIdx
    if (nextVisibleIdx !== undefined) {
      for (const s of all) {
        if (
          s.index < nextVisibleIdx &&
          (prevVisibleIdx === undefined || s.index >= prevVisibleIdx)
        ) {
          realPrev = s.index
        }
      }
    }

    let realNext = nextVisibleIdx
    if (prevVisibleIdx !== undefined) {
      for (const s of all) {
        if (
          s.index > prevVisibleIdx &&
          (nextVisibleIdx === undefined || s.index <= nextVisibleIdx)
        ) {
          realNext = s.index
          break
        }
      }
    }

    return { realPrev, realNext }
  }

  return { realPrev: prevVisibleIdx, realNext: nextVisibleIdx }
}
